import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { MailConnection, MailImportAs, MailProviderName } from "~/api/mailTypes";
import { useApi } from "~/api/provider";
import {
  useCollections,
  useImportMail,
  useMailActivity,
  useMailConnections,
  useMailInbox,
  useMailProviders,
  useRemoveMailConnection,
  useSendMail,
  useStartMailConnect,
  useSyncMailConnection,
  useUpdateMailConnection,
} from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

interface WorkProjectOption {
  id: string;
  name: string;
}

const SELECT =
  "h-8 min-w-0 rounded-sm border border-input-border bg-bg px-2 text-body text-text disabled:opacity-50";
const INPUT = "h-8 w-40 rounded-sm border border-input-border bg-bg px-2 text-body text-text";

/**
 * `?mailPending=<code>` / `?mailError=<code>` — read once on arrival, then stripped from the URL. A
 * pending code is FINISHED here, as the signed-in person: the server stores the mailbox only if this
 * person started the connection (a forwarded consent link connects nothing).
 */
function useReturnFromProvider() {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const url = new URL(window.location.href);
    const pending = url.searchParams.get("mailPending");
    const error = url.searchParams.get("mailError");
    if (!pending && !error) return;
    url.searchParams.delete("mailPending");
    url.searchParams.delete("mailError");
    window.history.replaceState(window.history.state, "", url.toString());
    if (error === "denied") toast.error(t("You cancelled the connection — nothing was connected."));
    else if (error === "scopes")
      toast.error(t("Docit needs permission to read your mail. Connect again and allow it."));
    else if (error) toast.error(t("The mailbox could not be connected. Try again."));
    if (pending) {
      api.mail.connections
        .connectFinish(pending)
        .then(() => {
          toast.success(t("Mailbox connected"));
          void queryClient.invalidateQueries({ queryKey: ["mail"] });
        })
        .catch((e: unknown) =>
          toast.error(
            e instanceof Error && e.message
              ? e.message
              : t("The mailbox could not be connected. Try again."),
          ),
        );
    }
  }, [t, api, queryClient]);
}

/**
 * Settings › Account › Mail: connect MY Gmail or Outlook mailbox to this workspace. Labelled mail
 * becomes knowledge or Work tiles, a reply to a Docit question email answers it, and Docit can
 * send as me. Everything here is personal — nobody else sees or uses this mailbox.
 */
export function Mail() {
  const { t } = useTranslation();
  const providers = useMailProviders();
  const connections = useMailConnections();
  const start = useStartMailConnect();
  const returnTo = useRouterState({ select: (s) => s.location.pathname });
  useReturnFromProvider();

  if (providers.isLoading || connections.isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const info = providers.data;
  const connect = (provider: MailProviderName) => start.mutate({ provider, returnTo });

  return (
    <div className="flex flex-col gap-5">
      <p className="max-w-[76ch] text-small text-text-secondary">
        {t(
          "Connect your own mailbox. Mail you label in it is saved as knowledge or filed as a Work tile, replying to a Docit question email answers it, and Docit can send mail as you. Only you see and use this mailbox.",
        )}
      </p>

      {info && !info.secretsAvailable ? (
        <Text variant="small">
          {t(
            "This server cannot store mailbox credentials safely yet (no secret key). An administrator needs to set one before a mailbox can be connected.",
          )}
        </Text>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {(info?.providers ?? []).map((p) => (
          <Button
            key={p.name}
            variant="neutral"
            disabled={!p.configured || !info?.secretsAvailable || start.isPending}
            title={p.configured ? undefined : t("Not set up on this server yet")}
            onClick={() => connect(p.name)}
          >
            {p.name === "google" ? t("Connect Gmail") : t("Connect Outlook / Microsoft 365")}
          </Button>
        ))}
      </div>
      {info?.providers.some((p) => !p.configured) ? (
        <Text variant="small">
          {t("A greyed-out button means that mail service is not set up on this server yet.")}
        </Text>
      ) : null}

      {(connections.data ?? []).length === 0 ? (
        <Text variant="small">{t("No mailbox connected yet.")}</Text>
      ) : (
        (connections.data ?? []).map((c) => (
          <ConnectionCard key={c.id} connection={c} onReconnect={() => connect(c.provider)} />
        ))
      )}
    </div>
  );
}

function ConnectionCard({
  connection: c,
  onReconnect,
}: {
  connection: MailConnection;
  onReconnect: () => void;
}) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const update = useUpdateMailConnection();
  const sync = useSyncMailConnection();
  const remove = useRemoveMailConnection();
  const { data: collections } = useCollections();
  const projects = useQuery({
    queryKey: ["work", "projects"],
    queryFn: async () => ((await api.work.projects()) as { data: WorkProjectOption[] }).data ?? [],
    staleTime: 60_000,
  });
  const [tab, setTab] = useState<"none" | "inbox" | "activity" | "send">("none");
  const [knowledgeLabel, setKnowledgeLabel] = useState(c.knowledge.label);
  const [tilesLabel, setTilesLabel] = useState(c.tiles.label);

  const patch = (input: Omit<Parameters<typeof update.mutate>[0], "id">) =>
    update.mutate({ id: c.id, ...input });
  const providerName = c.provider === "google" ? "Gmail" : "Outlook";
  const statusText =
    c.status === "reauth"
      ? t("Needs reconnecting")
      : c.status === "disabled"
        ? t("Paused")
        : c.status === "failing"
          ? t("Last check failed")
          : t("Connected");

  return (
    <section className="flex flex-col gap-1 rounded-md border border-divider p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-body font-medium text-text">
            {c.address} <span className="text-small text-text-tertiary">· {providerName}</span>
          </p>
          <p className="text-small text-text-secondary">
            {statusText}
            {c.lastSyncAt
              ? ` · ${t("checked {{when}}", { when: formatRelativeTime(c.lastSyncAt, i18n.language) })}`
              : ""}
          </p>
          {c.lastError ? <p className="text-small text-danger">{c.lastError}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {c.status === "reauth" ? (
            <Button size="sm" variant="primary" onClick={onReconnect}>
              {t("Reconnect")}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="neutral"
              disabled={sync.isPending || c.status === "disabled"}
              onClick={() =>
                sync.mutate(c.id, {
                  onSuccess: (r) =>
                    toast.success(
                      t("Checked: {{knowledge}} saved, {{tiles}} tiles, {{answers}} answers", {
                        knowledge: r.knowledge,
                        tiles: r.tiles,
                        answers: r.answers,
                      }),
                    ),
                })
              }
            >
              {sync.isPending ? t("Checking…") : t("Check now")}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => patch({ paused: c.status !== "disabled" })}
          >
            {c.status === "disabled" ? t("Resume") : t("Pause")}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t("Disconnect {{address}}? What it already saved stays.", { address: c.address }),
                )
              ) {
                remove.mutate(c.id, { onSuccess: () => toast.success(t("Mailbox disconnected")) });
              }
            }}
          >
            {t("Disconnect")}
          </Button>
        </div>
      </div>

      <div className="divide-y divide-divider">
        <div>
          <Switch
            checked={c.knowledge.enabled}
            disabled={!c.knowledge.collectionId}
            onCheckedChange={(on) => patch({ knowledgeEnabled: on })}
            label={t("Save labelled mail as knowledge")}
            description={t(
              "Mail you label “{{label}}” becomes a document in the collection you choose, searchable by everyone who can read it.",
              { label: c.knowledge.label },
            )}
          />
          <div className="flex flex-wrap items-center gap-2 pb-3">
            <select
              aria-label={t("Collection")}
              className={SELECT}
              value={c.knowledge.collectionId ?? ""}
              onChange={(e) => e.target.value && patch({ knowledgeCollectionId: e.target.value })}
            >
              <option value="">{t("Choose a collection")}</option>
              {(collections ?? []).map((col) => (
                <option key={col.id} value={col.id}>
                  {col.name}
                </option>
              ))}
            </select>
            <input
              aria-label={t("Label")}
              className={INPUT}
              value={knowledgeLabel}
              onChange={(e) => setKnowledgeLabel(e.target.value)}
              onBlur={() =>
                knowledgeLabel.trim() &&
                knowledgeLabel !== c.knowledge.label &&
                patch({ knowledgeLabel })
              }
            />
          </div>
        </div>

        <div>
          <Switch
            checked={c.tiles.enabled}
            disabled={!c.tiles.projectId}
            onCheckedChange={(on) => patch({ tilesEnabled: on })}
            label={t("File labelled mail as Work tiles")}
            description={t("Mail you label “{{label}}” becomes a task in the project you choose.", {
              label: c.tiles.label,
            })}
          />
          <div className="flex flex-wrap items-center gap-2 pb-3">
            <select
              aria-label={t("Project")}
              className={SELECT}
              value={c.tiles.projectId ?? ""}
              onChange={(e) => e.target.value && patch({ tilesProjectId: e.target.value })}
            >
              <option value="">{t("Choose a project")}</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <input
              aria-label={t("Label")}
              className={INPUT}
              value={tilesLabel}
              onChange={(e) => setTilesLabel(e.target.value)}
              onBlur={() =>
                tilesLabel.trim() && tilesLabel !== c.tiles.label && patch({ tilesLabel })
              }
            />
          </div>
        </div>

        <Switch
          checked={c.repliesEnabled}
          onCheckedChange={(on) => patch({ repliesEnabled: on })}
          label={t("Answer Docit questions by replying")}
          description={t(
            "When a question email reaches you, just reply to it. Docit reads your reply from your sent mail and answers the question as you.",
          )}
        />
        <Switch
          checked={c.sendEnabled && c.canSend}
          disabled={!c.canSend}
          onCheckedChange={(on) => patch({ sendEnabled: on })}
          label={t("Let Docit send mail as me")}
          description={t(
            "Messages you send from Docit go out from this mailbox and appear in your sent mail.",
          )}
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-2">
        {(["inbox", "activity", "send"] as const).map((k) => (
          <Button
            key={k}
            size="sm"
            variant={tab === k ? "primary" : "ghost"}
            onClick={() => setTab(tab === k ? "none" : k)}
          >
            {k === "inbox"
              ? t("Recent mail")
              : k === "activity"
                ? t("What Docit did")
                : t("Send a message")}
          </Button>
        ))}
      </div>
      {tab === "inbox" ? <InboxList connection={c} /> : null}
      {tab === "activity" ? <ActivityList connectionId={c.id} /> : null}
      {tab === "send" ? <Composer connection={c} /> : null}
    </section>
  );
}

function InboxList({ connection: c }: { connection: MailConnection }) {
  const { t, i18n } = useTranslation();
  const inbox = useMailInbox(c.id, c.status !== "disabled" && c.status !== "reauth");
  const take = useImportMail();

  if (c.status === "disabled" || c.status === "reauth") {
    return <Text variant="small">{t("Resume or reconnect the mailbox to see its mail.")}</Text>;
  }
  if (inbox.isLoading) return <Spinner label={t("Loading")} />;
  if (inbox.isError)
    return <Text variant="small">{t("The mailbox could not be read just now.")}</Text>;

  const run = (messageId: string, as: MailImportAs) =>
    take.mutate(
      { id: c.id, messageId, as },
      {
        onSuccess: (r) =>
          toast.success(
            r.workItemKey
              ? t("Filed as {{key}}", { key: r.workItemKey })
              : t("Saved as a document"),
          ),
      },
    );

  return (
    <ul className="flex flex-col divide-y divide-divider">
      {(inbox.data ?? []).map((m) => (
        <li key={m.id} className="flex flex-wrap items-start justify-between gap-2 py-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-body text-text">{m.subject || t("(no subject)")}</p>
            <p className="truncate text-small text-text-tertiary">
              {m.from}
              {m.at ? ` · ${formatRelativeTime(m.at, i18n.language)}` : ""}
            </p>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={Boolean(m.documentId) || !c.knowledge.collectionId || take.isPending}
              title={c.knowledge.collectionId ? undefined : t("Choose a collection above first")}
              onClick={() => run(m.id, "knowledge")}
            >
              {m.documentId ? t("Saved") : t("Save")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={Boolean(m.workItemId) || !c.tiles.projectId || take.isPending}
              title={c.tiles.projectId ? undefined : t("Choose a project above first")}
              onClick={() => run(m.id, "tile")}
            >
              {m.workItemId ? t("Filed") : t("Make a tile")}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

function ActivityList({ connectionId }: { connectionId: string }) {
  const { t, i18n } = useTranslation();
  const activity = useMailActivity(connectionId);
  if (activity.isLoading) return <Spinner label={t("Loading")} />;
  const rows = activity.data ?? [];
  if (rows.length === 0) return <Text variant="small">{t("Nothing yet.")}</Text>;
  return (
    <ul className="flex flex-col divide-y divide-divider">
      {rows.map((r) => (
        <li key={r.id} className="py-2">
          <p className="truncate text-body text-text">{r.subject || t("(no subject)")}</p>
          <p className="text-small text-text-secondary">
            {r.purpose === "knowledge"
              ? t("Saved as knowledge")
              : r.purpose === "tile"
                ? t("Filed as a tile")
                : t("Read as an answer")}
            {r.workItemKey ? ` · ${r.workItemKey}` : ""}
            {r.outcome !== "done"
              ? ` · ${r.outcome === "failed" ? t("refused") : t("not used")}`
              : ""}
            {` · ${formatRelativeTime(r.at, i18n.language)}`}
          </p>
          {r.note ? <p className="text-small text-text-tertiary">{r.note}</p> : null}
        </li>
      ))}
    </ul>
  );
}

function Composer({ connection: c }: { connection: MailConnection }) {
  const { t } = useTranslation();
  const send = useSendMail();
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  if (!c.canSend || !c.sendEnabled) {
    return <Text variant="small">{t("Sending is switched off for this mailbox.")}</Text>;
  }
  const recipients = to
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <form
      className="flex flex-col gap-2 pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        send.mutate(
          { id: c.id, to: recipients, subject, body },
          {
            onSuccess: () => {
              toast.success(t("Sent from {{address}}", { address: c.address }));
              setTo("");
              setSubject("");
              setBody("");
            },
          },
        );
      }}
    >
      <input
        aria-label={t("To")}
        placeholder={t("To")}
        className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <input
        aria-label={t("Subject")}
        placeholder={t("Subject")}
        className="h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <textarea
        aria-label={t("Message")}
        placeholder={t("Message (markdown)")}
        rows={6}
        className="rounded-sm border border-input-border bg-bg p-2 text-body text-text"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <div>
        <Button
          type="submit"
          variant="primary"
          disabled={send.isPending || recipients.length === 0 || !subject.trim()}
        >
          {send.isPending ? t("Sending…") : t("Send")}
        </Button>
      </div>
    </form>
  );
}
